import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '@/components/utils/translations';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { getProfileProcurementCategories, toOfficialCategoryName, toCategoryKeyForApi } from '@/utils/edtechCategoryNames';
import {
  fetchProcurementCategoryOptionsForTenant,
  mergeCategoryOptionsWithSaved,
} from '@/lib/procurementCategoryOptions';
import { tenantHasProcurementCategories } from '@/lib/procurementCompetitors';
import { Plus, Trash2, Save, Loader2, Target } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import {
  fetchProductProfiles,
  createProductProfile,
  updateProductProfile,
  activateProductProfile,
  deactivateProductProfile,
  deleteProductProfile,
} from '@/lib/api/fit';
import { toast } from 'sonner';
import {
  incumbentFieldsFromProfile,
  incumbentLinesFromFormText,
  logProductProfile,
  PRODUCT_PROFILE_FE_BUILD,
} from '@/lib/productProfileDebug';

function toStringArray(value) {
  if (Array.isArray(value)) {
    return value.map((v) => String(v)).filter(Boolean);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((v) => String(v)).filter(Boolean);
      }
    } catch {
      // ignore malformed JSON and fall through
    }

    return trimmed
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  return [];
}

function toObject(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      // ignore malformed JSON
    }
  }

  return {};
}

function parseNegativeSignals(text) {
  return String(text || '')
    .split('\n')
    .filter((line) => line.trim())
    .map((line) => {
      const trimmed = line.trim();

      if (trimmed.toLowerCase().startsWith('hard:')) {
        return { phrase: trimmed.slice(5).trim(), severity: 'hard' };
      }

      if (trimmed.toLowerCase().startsWith('soft:')) {
        return { phrase: trimmed.slice(5).trim(), severity: 'soft' };
      }

      return { phrase: trimmed, severity: 'soft' };
    })
    .filter((s) => s.phrase);
}

function normalizeNegativeSignals(signals) {
  if (Array.isArray(signals)) {
    return signals
      .map((s) => {
        if (typeof s === 'string') {
          return { phrase: s, severity: 'soft' };
        }

        if (s && typeof s === 'object') {
          return {
            phrase: String(s.phrase || '').trim(),
            severity: String(s.severity || 'soft').toLowerCase() === 'hard' ? 'hard' : 'soft',
          };
        }

        return null;
      })
      .filter((s) => s && s.phrase);
  }

  if (typeof signals === 'string') {
    const trimmed = signals.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      return normalizeNegativeSignals(parsed);
    } catch {
      return parseNegativeSignals(trimmed);
    }
  }

  return [];
}

function formatNegativeSignals(signals) {
  return normalizeNegativeSignals(signals)
    .map((s) => `${s.severity === 'hard' ? 'Hard' : 'Soft'}: ${s.phrase}`)
    .join('\n');
}

function tagInputValue(value) {
  return toStringArray(value).join(', ');
}

function lineInputValue(value) {
  return toStringArray(value).join('\n');
}

function tagInputParse(str) {
  return String(str || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

export default function SettingsProductProfile({ currentTenant: currentTenantProp }) {
  const { t } = useTranslation();
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const currentTenant = currentTenantProp || contextTenant;

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [form, setForm] = useState({
    name: '',
    selectedCategories: [],
    strong_fit_signals: '',
    negative_signals: '',
    incumbent_boost_signals: '',
    incumbent_penalty_signals: '',
    deployment_cloud: false,
    deployment_self_hosted: false,
    deployment_hybrid: false,
    integrations: '',
    security: '',
    key_features: '',
  });

  const [effectiveTenant, setEffectiveTenant] = useState(currentTenant || contextTenant || 'instructure');
  const [procurementCategoryOptions, setProcurementCategoryOptions] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoryOptionsSource, setCategoryOptionsSource] = useState('loading');

  const [categoryGridCols, setCategoryGridCols] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches ? 2 : 1,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const update = () => setCategoryGridCols(mq.matches ? 2 : 1);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const categoryPickerOptions = useMemo(
    () => mergeCategoryOptionsWithSaved(procurementCategoryOptions, form.selectedCategories),
    [procurementCategoryOptions, form.selectedCategories],
  );

  const categoryGridRows = useMemo(
    () => Math.ceil(categoryPickerOptions.length / categoryGridCols),
    [categoryPickerOptions.length, categoryGridCols],
  );

  const showProcurementCategories = useMemo(
    () => tenantHasProcurementCategories(effectiveTenant),
    [effectiveTenant],
  );

  useEffect(() => {
    logProductProfile('settings.mount', {
      build: PRODUCT_PROFILE_FE_BUILD,
      effectiveTenant,
      currentTenant,
      editingId,
      uiIncumbentLines: incumbentLinesFromFormText(
        form.incumbent_boost_signals,
        form.incumbent_penalty_signals,
      ),
    });
  }, []);

  const loadProfiles = async () => {
    if (!contextUser) return;

    setLoading(true);
    try {
      const tenantId = resolveEffectiveTenant(
        contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
        currentTenant,
        contextUser,
        'instructure'
      );

      setEffectiveTenant(tenantId);

      logProductProfile('settings.load_profiles.start', { tenantId });
      const list = await fetchProductProfiles(tenantId);
      const profilesList = Array.isArray(list) ? list : [];
      setProfiles(profilesList);
      logProductProfile('settings.load_profiles.ok', {
        tenantId,
        count: profilesList.length,
        profileIds: profilesList.map((p) => p.id),
      });
    } catch (err) {
      console.error('[ProductProfile] settings.load_profiles.error', err);
      logProductProfile('settings.load_profiles.error', {
        message: err?.message || String(err),
      });
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfiles();
  }, [contextUser, currentTenant]);

  useEffect(() => {
    if (!effectiveTenant) return;
    let cancelled = false;
    setCategoriesLoading(true);
    fetchProcurementCategoryOptionsForTenant(effectiveTenant).then((result) => {
      if (cancelled) return;
      setProcurementCategoryOptions(result.options || []);
      setCategoryOptionsSource(result.source || 'unknown');
      setCategoriesLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [effectiveTenant]);

  const resetForm = () => {
    logProductProfile('settings.form.reset');
    setEditingId(null);
    setForm({
      name: '',
      selectedCategories: [],
      strong_fit_signals: '',
      negative_signals: '',
      incumbent_boost_signals: '',
      incumbent_penalty_signals: '',
      deployment_cloud: false,
      deployment_self_hosted: false,
      deployment_hybrid: false,
      integrations: '',
      security: '',
      key_features: '',
    });
  };

  const loadIntoForm = (profile) => {
    const capabilities = toObject(profile?.capabilities);
    const deploymentModels = toStringArray(capabilities?.deployment_models);
    const apiIncumbent = incumbentFieldsFromProfile(profile);

    logProductProfile('settings.form.load', {
      profileId: profile?.id,
      profileName: profile?.name,
      apiIncumbent,
    });

    setEditingId(profile.id);
    const stored = getProfileProcurementCategories(profile).map(
      (c) => toOfficialCategoryName(c) || c,
    );
    const selectedCategories = [...new Set(stored)];

    setForm({
      name: profile?.name || '',
      selectedCategories,
      strong_fit_signals: lineInputValue(profile?.strong_fit_signals),
      negative_signals: formatNegativeSignals(profile?.negative_signals),
      incumbent_boost_signals: lineInputValue(profile?.incumbent_boost_signals),
      incumbent_penalty_signals: lineInputValue(profile?.incumbent_penalty_signals),
      deployment_cloud: deploymentModels.includes('cloud'),
      deployment_self_hosted: deploymentModels.includes('self-hosted'),
      deployment_hybrid: deploymentModels.includes('hybrid'),
      integrations: tagInputValue(capabilities?.integrations),
      security: tagInputValue(capabilities?.security),
      key_features: tagInputValue(capabilities?.key_features),
    });

    const hydratedBoost = lineInputValue(profile?.incumbent_boost_signals);
    const hydratedPenalty = lineInputValue(profile?.incumbent_penalty_signals);
    logProductProfile('settings.form.hydrated', {
      profileId: profile?.id,
      textareaLines: incumbentLinesFromFormText(hydratedBoost, hydratedPenalty),
      apiIncumbent,
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;

    setSaving(true);
    try {
      const deployment_models = [];
      if (form.deployment_cloud) deployment_models.push('cloud');
      if (form.deployment_self_hosted) deployment_models.push('self-hosted');
      if (form.deployment_hybrid) deployment_models.push('hybrid');

      const selected = [];
      const seen = new Set();
      for (const c of form.selectedCategories || []) {
        const official = toOfficialCategoryName(c) || String(c).trim();
        if (!official) continue;
        const key = toCategoryKeyForApi(official);
        if (seen.has(key)) continue;
        seen.add(key);
        selected.push(official);
      }
      const payload = {
        name: form.name.trim(),
        category_primary: selected[0] || null,
        category_tags: selected,
        strong_fit_signals: String(form.strong_fit_signals || '')
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
        negative_signals: parseNegativeSignals(form.negative_signals),
        incumbent_boost_signals: String(form.incumbent_boost_signals || '')
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
        incumbent_penalty_signals: String(form.incumbent_penalty_signals || '')
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
        capabilities: {
          deployment_models,
          integrations: tagInputParse(form.integrations),
          security: tagInputParse(form.security),
          key_features: tagInputParse(form.key_features),
        },
      };

      logProductProfile('settings.save.start', {
        tenantId: effectiveTenant,
        editingId,
        mode: editingId ? 'update' : 'create',
        payloadIncumbent: incumbentFieldsFromProfile(payload),
        formTextareaLines: incumbentLinesFromFormText(
          form.incumbent_boost_signals,
          form.incumbent_penalty_signals,
        ),
      });

      let saved;
      if (editingId) {
        saved = await updateProductProfile(effectiveTenant, editingId, payload);
      } else {
        saved = await createProductProfile(effectiveTenant, payload);
      }

      logProductProfile('settings.save.ok', {
        tenantId: effectiveTenant,
        profileId: saved?.id || editingId,
        responseIncumbent: incumbentFieldsFromProfile(saved),
      });

      resetForm();
      await loadProfiles();
    } catch (err) {
      console.error('[ProductProfile] settings.save.error', err);
      logProductProfile('settings.save.error', {
        editingId,
        message: err?.message || String(err),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (id) => {
    try {
      logProductProfile('settings.activate', { id, effectiveTenant });
      await activateProductProfile(effectiveTenant, id);
      await loadProfiles();
    } catch (err) {
      console.error('Activate failed:', err);
      toast.error(t('productFitActivateError'), {
        description: err?.message || String(err),
      });
    }
  };

  const handleDeactivate = async (id) => {
    try {
      await deactivateProductProfile(effectiveTenant, id);
      await loadProfiles();
    } catch (err) {
      console.error('Deactivate failed:', err);
      toast.error(t('productFitDeactivateError'), {
        description: err?.message || String(err),
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteProductProfile(effectiveTenant, id);
      setDeleteConfirm(null);
      resetForm();
      await loadProfiles();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  if (!contextUser) {
    return (
      <div className="flex items-center gap-2 text-[#5C6B7A] py-8">
        <Loader2 className="w-5 h-5 animate-spin" />
        {t('loadingProductProfiles')}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[#5C6B7A] py-8">
        <Loader2 className="w-5 h-5 animate-spin" />
        {t('loadingProductProfiles')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-[#5C6B7A]">{t('productFitProfileDesc')}</p>
        <Button
          onClick={() => resetForm()}
          className="bg-[#00C2C2] hover:bg-[#00A3A3] text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('createProfile')}
        </Button>
      </div>

      <Card className="border border-gray-200">
        <CardHeader>
          <CardTitle className="text-lg text-[#0A1A2F]">
            {editingId ? t('editProfile') : t('newProfile')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="name">{t('productFitName')}</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder={t('productFitNameExample')}
              className="mt-1"
            />
          </div>

          {showProcurementCategories && (
          <div>
            <Label className="mb-1 block">{t('productFitCategories')}</Label>
            <p className="text-xs text-[#5C6B7A] mb-2">
              {categoriesLoading
                ? t('productFitCategoriesLoading')
                : categoryOptionsSource === 'bootstrap'
                  ? t('productFitCategoriesDesc')
                  : t('productFitCategoriesFallback')}
            </p>
            {categoriesLoading ? (
              <div className="flex items-center gap-2 text-sm text-[#5C6B7A] py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('productFitCategoriesLoading')}
              </div>
            ) : (
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[280px] overflow-y-auto border border-gray-200 rounded-md p-3"
              style={{
                gridAutoFlow: 'column',
                gridTemplateRows: `repeat(${categoryGridRows}, auto)`,
              }}
            >
              {categoryPickerOptions.map((category) => {
                const categoryKey = toCategoryKeyForApi(category);
                const checked = (form.selectedCategories || []).some(
                  (c) => toCategoryKeyForApi(c) === categoryKey,
                );
                return (
                  <label key={categoryKey || category} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(isChecked) => {
                        setForm((f) => ({
                          ...f,
                          selectedCategories: isChecked
                            ? [...f.selectedCategories.filter((c) => toCategoryKeyForApi(c) !== categoryKey), category]
                            : f.selectedCategories.filter((c) => toCategoryKeyForApi(c) !== categoryKey),
                        }));
                      }}
                    />
                    <span>{category}</span>
                  </label>
                );
              })}
            </div>
            )}
            {!categoriesLoading && form.selectedCategories.length === 0 && (
              <p className="text-xs text-[#5C6B7A] mt-2">{t('productFitNoCategoriesSelected')}</p>
            )}
          </div>
          )}

          <div>
            <Label htmlFor="strong_fit_signals">{t('productFitStrongSignals')}</Label>
            <Textarea
              id="strong_fit_signals"
              value={form.strong_fit_signals}
              onChange={(e) => setForm((f) => ({ ...f, strong_fit_signals: e.target.value }))}
              placeholder={t('productFitStrongSignalsExample')}
              rows={4}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="negative_signals">{t('productFitNegativeSignals')}</Label>
            <Textarea
              id="negative_signals"
              value={form.negative_signals}
              onChange={(e) => setForm((f) => ({ ...f, negative_signals: e.target.value }))}
              placeholder={t('productFitNegativeSignalsExample')}
              rows={4}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="incumbent_boost_signals">Strong likelihood to replace</Label>
            <p className="text-xs text-[#5C6B7A] mt-0.5 mb-1">
              One incumbent keyword per line — competitors you displace easily. Substring match (e.g. &quot;Pearson&quot; matches &quot;Pearson VUE&quot;, &quot;Pearson Onvue&quot;). Each match adds +10 to the fit score.
            </p>
            <Textarea
              id="incumbent_boost_signals"
              value={form.incumbent_boost_signals}
              onChange={(e) => setForm((f) => ({ ...f, incumbent_boost_signals: e.target.value }))}
              placeholder={`Pearson\nExamity\nMonitorEDU`}
              rows={4}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="incumbent_penalty_signals">Hard to replace</Label>
            <p className="text-xs text-[#5C6B7A] mt-0.5 mb-1">
              One incumbent keyword per line — entrenched players you struggle to displace. Each match subtracts 18 from the fit score. Anything not listed here or above is treated as Indifferent (no impact).
            </p>
            <Textarea
              id="incumbent_penalty_signals"
              value={form.incumbent_penalty_signals}
              onChange={(e) => setForm((f) => ({ ...f, incumbent_penalty_signals: e.target.value }))}
              placeholder={`ProctorU\nHonorlock\nRespondus`}
              rows={4}
              className="mt-1"
            />
          </div>

          <div>
            <Label className="mb-2 block">{t('productFitDeploymentModels')}</Label>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={form.deployment_cloud}
                  onCheckedChange={(c) => setForm((f) => ({ ...f, deployment_cloud: !!c }))}
                />
                <span className="text-sm">{t('productFitCloud')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={form.deployment_self_hosted}
                  onCheckedChange={(c) => setForm((f) => ({ ...f, deployment_self_hosted: !!c }))}
                />
                <span className="text-sm">{t('productFitSelfHosted')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={form.deployment_hybrid}
                  onCheckedChange={(c) => setForm((f) => ({ ...f, deployment_hybrid: !!c }))}
                />
                <span className="text-sm">{t('productFitHybrid')}</span>
              </label>
            </div>
          </div>

          <div>
            <Label htmlFor="integrations">{t('productFitIntegrations')}</Label>
            <Input
              id="integrations"
              value={form.integrations}
              onChange={(e) => setForm((f) => ({ ...f, integrations: e.target.value }))}
              placeholder={t('productFitIntegrationsExample')}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="security">{t('productFitSecurity')}</Label>
            <Input
              id="security"
              value={form.security}
              onChange={(e) => setForm((f) => ({ ...f, security: e.target.value }))}
              placeholder={t('productFitSecurityExample')}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="key_features">{t('productFitKeyFeatures')}</Label>
            <Input
              id="key_features"
              value={form.key_features}
              onChange={(e) => setForm((f) => ({ ...f, key_features: e.target.value }))}
              placeholder={t('productFitKeyFeaturesExample')}
              className="mt-1"
            />
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              disabled={saving || !form.name.trim()}
              className="bg-[#00C2C2] hover:bg-[#00A3A3] text-white"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {editingId ? t('productFitUpdate') : t('productFitCreate')}
            </Button>

            {editingId && (
              <Button variant="outline" onClick={resetForm}>
                {t('cancel')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <h3 className="text-base font-semibold text-[#0A1A2F]">{t('productFitProfiles')}</h3>

        {profiles.length === 0 ? (
          <p className="text-[#5C6B7A] text-sm">{t('productFitNoProfiles')}</p>
        ) : (
          <div className="space-y-2">
            {profiles.map((p) => {
              const profileCategories = getProfileProcurementCategories(p);
              return (
              <Card key={p.id} className="border border-gray-200">
                <CardContent className="py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Target className="w-5 h-5 text-[#00C2C2]" />
                    <div>
                      <p className="font-medium text-[#0A1A2F]">{p.name}</p>
                      <p className="text-sm text-[#5C6B7A]">
                        {profileCategories.length ? profileCategories.join(', ') : '—'} · v
                        {p.version ?? 1} · {formatDate(p.updated_at)}
                      </p>
                    </div>
                    {p.is_active && (
                      <Badge className="bg-green-100 text-green-800 border-green-200">
                        {t('productFitActive')}
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {!p.is_active && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleActivate(p.id)}
                        className="border-[#00C2C2] text-[#00C2C2] hover:bg-[#00C2C2]/10"
                      >
                        {t('productFitSetAsActive')}
                      </Button>
                    )}
                    {p.is_active && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeactivate(p.id)}
                        className="border-gray-300 text-gray-700 hover:bg-gray-50"
                      >
                        {t('productFitDeactivate')}
                      </Button>
                    )}

                    <Button size="sm" variant="outline" onClick={() => loadIntoForm(p)}>
                      {t('editProfile')}
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => setDeleteConfirm(p)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
            })}
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('productFitDeleteProfile')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('productFitDeleteConfirm', { name: deleteConfirm?.name || '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirm && handleDelete(deleteConfirm.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}